import {
  Accessibility,
  AlarmClock,
  Bed,
  Heart,
  PiggyBank,
  SquarePlus,
  TriangleAlert,
  Users,
} from "lucide-react";

/**
 * Patient tags: 8 patient-LEVEL attributes describing who staff are talking to (replaces the
 * old descriptive thread flags). Set once on the patient record (patientMeta.tags via the
 * admin users API) and surfaced read-only onto every thread row as `thread.patientTags` by
 * the backend's enrichment — they ride along every conversation that patient has.
 *
 * Tags describe, they never compete with workflow status: a thread can be "Assigned" AND
 * "Patient we love" AND "Surgery patient" at once. In the chronological list they stay quiet
 * glyphs, never colored section blocks.
 *
 * NOTE (Tailwind v4): every class below is a complete literal string — the scanner cannot see
 * constructed names like `text-${tone}-500` (same constraint as sidebar-icon-colors.js).
 */
export const PATIENT_TAG_CATALOG = [
  {
    key: "wheelchair",
    label: "Wheelchair bound",
    description: "Needs step-free access — flag rooms & timing.",
    icon: Accessibility,
    glyphClassName: "text-sky-500 dark:text-sky-400",
    chipClassName:
      "border border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-950/60 dark:text-sky-300",
  },
  {
    key: "loved",
    label: "Patient we love",
    description: "A treasured regular — give them extra warmth.",
    icon: Heart,
    glyphClassName: "text-rose-500 dark:text-rose-400",
    chipClassName:
      "border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-300",
  },
  {
    key: "inpatient",
    label: "Inpatient",
    description: "Currently admitted — replies are time-critical.",
    icon: AlarmClock,
    glyphClassName: "text-orange-500 dark:text-orange-400",
    chipClassName:
      "border border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-500/40 dark:bg-orange-950/60 dark:text-orange-300",
  },
  {
    key: "trouble",
    label: "*TLC*",
    description: "Handle with care; loop in a team lead.",
    icon: TriangleAlert,
    glyphClassName: "text-red-500 dark:text-red-400",
    chipClassName:
      "border border-red-200 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-950/60 dark:text-red-300",
  },
  {
    key: "cost",
    label: "Cost-sensitive",
    description: "Lead with price and offer options.",
    icon: PiggyBank,
    glyphClassName: "text-amber-500 dark:text-amber-400",
    chipClassName:
      "border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-300",
  },
  {
    key: "hcp",
    label: "Healthcare professional",
    description: "Speaks the language — you can be concise.",
    icon: SquarePlus,
    glyphClassName: "text-teal-500 dark:text-teal-400",
    chipClassName:
      "border border-teal-200 bg-teal-50 text-teal-700 dark:border-teal-500/40 dark:bg-teal-950/60 dark:text-teal-300",
  },
  {
    key: "friend",
    label: "Friend of a doctor",
    description: "Personal connection to a clinician.",
    icon: Users,
    glyphClassName: "text-violet-500 dark:text-violet-400",
    chipClassName:
      "border border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-300",
  },
  {
    key: "surgery",
    label: "Surgery patient",
    description: "Pre/post-op — follow the surgical pathway.",
    icon: Bed,
    glyphClassName: "text-fuchsia-500 dark:text-fuchsia-400",
    chipClassName:
      "border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 dark:border-fuchsia-500/40 dark:bg-fuchsia-950/60 dark:text-fuchsia-300",
  },
];

const byKey = Object.fromEntries(PATIENT_TAG_CATALOG.map((def) => [def.key, def]));

/** The thread row's server-enriched patient tags (canonical catalog keys; unknowns filtered). */
export function getPatientTagStrings(thread) {
  const raw = thread?.patientTags;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((t) => String(t || "").trim()).filter((t) => Boolean(byKey[t]));
}

export function getPatientTagDefinition(key) {
  return byKey[String(key || "").trim()];
}
